import React, { useState, useEffect, useRef, useCallback } from "react";
import countries from "../data/countries";
import { CONTINENTS } from "../data/constants";
import QuizMap from "../components/QuizMap";

function flagEmoji(code) {
  if (!code || code.length !== 2) return "";
  try {
    return [...code.toUpperCase()].map(c =>
      String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
    ).join("");
  } catch {
    return "";
  }
}

function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const QUIZ_CONTS = ["World", ...CONTINENTS.filter(c => c !== "All")];

export default function QuizPage() {
  const [phase,          setPhase]          = useState("setup");
  const [selCont,        setSelCont]        = useState("World");
  const [questions,      setQuestions]      = useState([]);
  const [qIdx,           setQIdx]           = useState(0);
  const [score,          setScore]          = useState(0);
  const [attempted,      setAttempted]      = useState(0);  // questions with a first guess made
  const [questionFailed, setQuestionFailed] = useState(false); // first guess on current question was wrong
  const [wrongCount,     setWrongCount]     = useState(0);
  const [wrongFeedback,  setWrongFeedback]  = useState(null);
  const [answeredCodes,  setAnsweredCodes]  = useState(new Set());
  const [flashCode,      setFlashCode]      = useState(null);
  const [flashColor,     setFlashColor]     = useState(null);
  const [elapsed,        setElapsed]        = useState(0);
  const [finalTime,      setFinalTime]      = useState(0);

  const timerRef       = useRef(null);
  const flashingRef    = useRef(false);
  const flashTimeoutRef = useRef(null);
  const elapsedRef     = useRef(0);

  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  const target = questions[qIdx] ?? null;

  // Timer
  useEffect(() => {
    if (phase !== "playing") return;
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Stop flash helper — stable, no deps
  const stopFlash = useCallback(() => {
    clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = null;
    flashingRef.current = false;
    setFlashCode(null);
    setFlashColor(null);
  }, []);

  const advance = useCallback(() => {
    stopFlash();
    setWrongCount(0);
    setWrongFeedback(null);
    setQuestionFailed(false);
    const next = qIdx + 1;
    if (next >= questions.length) {
      clearInterval(timerRef.current);
      setFinalTime(elapsedRef.current);
      setPhase("done");
    } else {
      setQIdx(next);
    }
  }, [qIdx, questions.length, stopFlash]);

  // Continuous flash: red 1s → white 1s → red 1s → … until correct click
  const startFlash = useCallback((code) => {
    if (flashingRef.current) return;
    flashingRef.current = true;

    const cycle = (color) => {
      if (!flashingRef.current) return;
      setFlashCode(code);
      setFlashColor(color);
      flashTimeoutRef.current = setTimeout(
        () => cycle(color === "red" ? "white" : "red"),
        800
      );
    };

    cycle("red");
  }, []);

  const handleClick = useCallback((code) => {
    if (!target) return;
    if (answeredCodes.has(code)) return;

    if (code === target.code) {
      // Correct — only scores if first guess was right
      stopFlash();
      if (!questionFailed) {
        setScore(s => s + 1);
        setAttempted(a => a + 1);
      }
      setAnsweredCodes(prev => new Set([...prev, code]));
      setWrongFeedback(null);
      advance();
    } else {
      // Wrong — ignore clicks while flash is running
      if (flashingRef.current) return;
      // First wrong guess on this question counts it as a miss
      if (!questionFailed) {
        setQuestionFailed(true);
        setAttempted(a => a + 1);
      }
      const wrong = countries.find(c => c.code === code);
      if (wrong) setWrongFeedback({ name: wrong.name, peak: wrong.peak });
      const nw = wrongCount + 1;
      setWrongCount(nw);
      if (nw >= 3) startFlash(target.code);
    }
  }, [target, answeredCodes, wrongCount, questionFailed, advance, startFlash, stopFlash]);

  const skip = useCallback(() => { advance(); }, [advance]);

  const quit = useCallback(() => {
    clearInterval(timerRef.current);
    stopFlash();
    setPhase("setup");
  }, [stopFlash]);

  const startQuiz = useCallback(() => {
    const pool = selCont === "World"
      ? [...countries]
      : countries.filter(c => c.continent === selCont);
    stopFlash();
    setQuestions(shuffle(pool));
    setQIdx(0);
    setScore(0);
    setAttempted(0);
    setQuestionFailed(false);
    setWrongCount(0);
    setWrongFeedback(null);
    setAnsweredCodes(new Set());
    setElapsed(0);
    setFinalTime(0);
    setPhase("playing");
  }, [selCont, stopFlash]);

  // ── Setup ─────────────────────────────────────────────────────────────
  if (phase === "setup") {
    return (
      <div className="qz-setup">
        <div className="qz-box">
          <div className="qz-badge">Quiz Mode</div>
          <h1 className="qz-title">Highpoint Quiz</h1>
          <p className="qz-desc">
            You'll be shown a peak name. 
            Click the correct country on the map to score.
          </p>
          <div className="ct" style={{ justifyContent: "center", marginBottom: 28 }}>
            {QUIZ_CONTS.map(c => {
              const n = c === "World"
                ? countries.length
                : countries.filter(x => x.continent === c).length;
              return (
                <button key={c} className={`ctb ${selCont === c ? "a" : ""}`} onClick={() => setSelCont(c)}>
                  {c} <span style={{ opacity: 0.65, fontSize: 14 }}>({n})</span>
                </button>
              );
            })}
          </div>
          <button className="qz-start-btn" onClick={startQuiz}>Start Quiz →</button>
        </div>
      </div>
    );
  }

  // ── Done ──────────────────────────────────────────────────────────────
  if (phase === "done") {
    const pct   = Math.round((score / questions.length) * 100);
    const medal = pct >= 90 ? "🏆" : pct >= 70 ? "🥈" : pct >= 50 ? "🥉" : "📍";
    return (
      <div className="qz-setup">
        <div className="qz-box">
          <div className="qz-medal">{medal}</div>
          <h1 className="qz-title">Quiz Complete!</h1>
          <div className="qz-done-stats">
            <div className="qz-done-stat">
              <div className="qz-done-v">{score}/{questions.length}</div>
              <div className="qz-done-l">Score</div>
            </div>
            <div className="qz-done-stat">
              <div className="qz-done-v">{pct}%</div>
              <div className="qz-done-l">Accuracy</div>
            </div>
            <div className="qz-done-stat">
              <div className="qz-done-v">{formatTime(finalTime)}</div>
              <div className="qz-done-l">Time</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 24, flexWrap: "wrap" }}>
            <button className="qz-start-btn" onClick={startQuiz}>Play Again</button>
            <button className="qz-start-btn qz-start-btn-sec" onClick={() => setPhase("setup")}>Change Region</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Playing ───────────────────────────────────────────────────────────
  const pct     = attempted > 0 ? Math.round((score / attempted) * 100) : 0;
  const mapCont = selCont === "World" ? "All" : selCont;
  // Flag hint appears after 2 consecutive wrong answers on the same question
  const showFlag = wrongCount >= 2 && target;

  return (
    <div className="qz-playing">

      {/* HUD bar */}
      <div className="qz-hud">
        <div className="qz-hud-l">
          <span className="qz-score">{score} / {questions.length}</span>
          <span className="qz-sep">|</span>
          <span className="qz-pct">{pct}%</span>
        </div>

        <div className="qz-hud-c">
          <span className="qz-prompt">Click on</span>
          <span className="qz-peakbox">{target?.peak}</span>
          <button className="qz-skip" onClick={skip} title="Skip question">▶|</button>
        </div>

        <div className="qz-hud-r">
          {showFlag
            ? <span className="qz-flag qz-flag-hint">{flagEmoji(target.code)}</span>
            : <span className="qz-flag-placeholder" />
          }
          <span className="qz-timerbox">{formatTime(elapsed)}</span>
          <button className="qz-quit" onClick={quit} title="Quit quiz">✕</button>
        </div>
      </div>

      {/* Wrong-click feedback */}
      {wrongFeedback && (
        <div className="qz-wrong">
          That's <strong>{wrongFeedback.name}</strong> — {wrongFeedback.peak}
          {wrongCount >= 3 && <span className="qz-revealing"> · Flashing the answer…</span>}
        </div>
      )}

      <QuizMap
        targetCode={target?.code}
        flashCode={flashCode}
        flashColor={flashColor}
        onCountryClick={handleClick}
        cont={mapCont}
        answeredCodes={answeredCodes}
      />
    </div>
  );
}
